import { MULTIMEDIA_TYPES, QUESTION_TYPE, ANSWER_TYPES } from '@/utils/constants';
import * as yup from 'yup';

export const playTypeRead_Schema = (supportedFormats: string[]) => yup.object({
  description: yup
    .string()
    .transform((value) => {
      if (!value) return ""

      const temp = document.createElement("div")
      temp.innerHTML = value
      return temp.textContent?.trim() || ""
    })
    .required('Descripción es requerida'),
  multimediaTypeId: yup.string().nullable(),
  fileMultimedia: yup
    .mixed()
    .nullable()
    .when(['urlMultimedia', 'multimediaTypeId'], {
      is: (urlMultimedia: any, multimediaTypeId: number) =>
        !urlMultimedia && multimediaTypeId && multimediaTypeId != MULTIMEDIA_TYPES.LINK,
      then: (schema) => schema
        .required('Archivo multimedia es requerido')
        .test('fileSize', function (value: any) {
          const { multimediaTypeId } = this.parent;
          const isVideo =
            multimediaTypeId == MULTIMEDIA_TYPES.VIDEO;
          const maxSize = isVideo
            ? 20 * 1024 * 1024
            : 2 * 1024 * 1024;
          if (!value || value.size <= maxSize) {
            return true;
          }
          return this.createError({
            message: isVideo
              ? 'El video no debe pesar más de 20MB'
              : 'La imagen no debe pesar más de 2MB',
          });
        })
        .test('fileType', 'Formato del archivo no es válido', (value: any) => {
          if (!value) return false;
          return supportedFormats.includes(value.type);
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
  link: yup
    .string()
    .when('multimediaTypeId', {
      is: (multimediaTypeId: number) => multimediaTypeId == MULTIMEDIA_TYPES.LINK,
      then: (schema) =>
        schema
          .required('El enlace es requerido')
          .url('Debe ser un enlace válido'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  steps: yup
    .string()
    .when('multimediaTypeId', {
      is: (multimediaTypeId: number) => multimediaTypeId == MULTIMEDIA_TYPES.LINK,
      then: (schema) =>
        schema
          .required('El texto del enlace es requerido'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
});

export const playTypeQuestion_Schema = (supportedFormats: string[]) => yup.object({
  questionTypeId: yup.string().required('Tipo de Pregunta es requerido'),
  sentence: yup.string().required('Enunciado de la Pregunta es requerido'),
  multimediaTypeId: yup.string().nullable(),
  fileMultimedia: yup
    .mixed()
    .nullable()
    .when(['urlMultimedia', 'multimediaTypeId'], {
      is: (urlMultimedia: any, multimediaTypeId: number) =>
        !urlMultimedia && multimediaTypeId && multimediaTypeId != MULTIMEDIA_TYPES.LINK,
      then: (schema) => schema
        .required('Archivo multimedia es requerido')
        .test('fileSize', function (value: any) {
          const { multimediaTypeId } = this.parent;
          const isVideo =
            multimediaTypeId == MULTIMEDIA_TYPES.VIDEO;
          const maxSize = isVideo
            ? 20 * 1024 * 1024
            : 2 * 1024 * 1024;
          if (!value || value.size <= maxSize) {
            return true;
          }
          return this.createError({
            message: isVideo
              ? 'El video no debe pesar más de 20MB'
              : 'La imagen no debe pesar más de 2MB',
          });
        })
        .test('fileType', 'Formato del archivo no es válido', (value: any) => {
          if (!value) return false;
          return supportedFormats.includes(value.type);
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
  link: yup
    .string()
    .when('multimediaTypeId', {
      is: (multimediaTypeId: number) => multimediaTypeId == MULTIMEDIA_TYPES.LINK,
      then: (schema) =>
        schema
          .required('El enlace es requerido')
          .url('Debe ser un enlace válido'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  steps: yup
    .string()
    .when('multimediaTypeId', {
      is: (multimediaTypeId: number) => multimediaTypeId == MULTIMEDIA_TYPES.LINK,
      then: (schema) =>
        schema
          .required('El texto del enlace es requerido'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  timer: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value
    )
    .nullable()
    .notRequired()
    .typeError("El valor debe ser numérico")
    .integer("El valor debe ser un número entero")
    .moreThan(0, "El valor debe ser mayor a 0"),
  questionOptions: yup.mixed().when('questionTypeId', ([questionTypeId]) => {
    if (questionTypeId === '2') {
      return yup
        .array()
        .min(1, 'Debe agregar al menos una opción.')
        .of(
          yup.object({
            id: yup.number().nullable(),
            option: yup.string().required('La opción es requerida.'),
            isCorrect: yup.boolean().required()  // <-- make sure we have isCorrect
          })
        )
        .test(
          'correct-options',
          function (value) {
                    const answerType = Number(this.parent.answerType);
        
            if (!value) {
              return this.createError({
                message: 'Debe marcar al menos una opción como correcta.',
              });
            }
        
            const totalCorrect =
              value.filter((item: any) => item.isCorrect).length;
              if (answerType !== ANSWER_TYPES.SELECCION_UNICA && answerType !== ANSWER_TYPES.SELECCION_MULTIPLE) {
              if (totalCorrect === 0) {
                return this.createError({
                  message: 'Debe marcar al menos una opción como correcta.',
                });
              }
              return true;
            }
        
            if (answerType === ANSWER_TYPES.SELECCION_UNICA && totalCorrect !== 1) {
              return this.createError({
                message: 'Debe marcar una sola opción como correcta.',
              });
            }
        
            if (answerType === ANSWER_TYPES.SELECCION_MULTIPLE && totalCorrect <= 1) {
              return this.createError({
                message: 'Debe marcar más de una opción como correcta.',
              });
            }
            return true;
          }
        );
    }
    return yup.array().notRequired();
  }),
  answer: yup.string().when("questionTypeId", {
    is: (val: number) => val == QUESTION_TYPE.SEMIOPEN, // condition
    then: (schema) => schema.required("Respuesta es requerida"),
    otherwise: (schema) => schema.notRequired(),
  }),
  answerType: yup.string().when("questionTypeId", {
    is: (val: number) => val == QUESTION_TYPE.CLOSED, // condition
    then: (schema) => schema.required("Tipo de respuesta es requerido"),
    otherwise: (schema) => schema.notRequired(),
  }),
});


export const questionOption_Schema = yup.object().shape({
  questionOptionText: yup
    .string()
    .required('*La respuesta es requerida.')   // required message
    .min(1, '*Debe tener al menos 3 caracteres.') // optional: minimum length
    .max(5000, '*Máximo 5000 caracteres.')         // optional: maximum length
});



export const playName_Schema = yup.object().shape({
  playName: yup
    .string()
    .required('*El nombre de la jugada es requerida.')   // required message
    .min(1, '*Debe tener al menos 3 caracteres.') // optional: minimum length
    .max(100, '*Máximo 100 caracteres.')         // optional: maximum length
});